import React, { useState, useEffect } from 'react';
import {
  User,
  Shield,
  Key,
  Bell,
  Lock,
  Eye,
  EyeOff,
  Smartphone,
  QrCode,
  Copy,
  Check,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Download,
  Calendar,
  Activity,
  Globe,
  Settings as SettingsIcon,
  Save,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';
import SystemSecuritySettings from '../components/SystemSecuritySettings';

interface MFASetup {
  qr_code: string;
  secret: string;
  backup_codes: string[];
}

interface UserActivity {
  id: number;
  action: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetup, setMfaSetup] = useState<MFASetup | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [recentActivity, setRecentActivity] = useState<UserActivity[]>([]);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Password validation function (same as backend)
  const validatePassword = (password: string) => {
    const errors: string[] = [];

    if (!password) {
      return { isValid: false, errors: ['Password is required'] };
    }

    if (password.length < 12) {
      errors.push('At least 12 characters long');
    }

    if (!/\d/.test(password)) {
      errors.push('At least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('At least one special character');
    }

    return { isValid: errors.length === 0, errors };
  };

  const handlePasswordChange = (password: string) => {
    setProfileData(prev => ({ ...prev, newPassword: password }));
    const validation = validatePassword(password);
    setPasswordErrors(validation.errors);
  };

  const [passwordMfaCode, setPasswordMfaCode] = useState('');
  const [showPasswordMfaInput, setShowPasswordMfaInput] = useState(false);
  const [ssoUser, setSsoUser] = useState(false);
  const [ssoProvider, setSsoProvider] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Ensure API has the latest token
    const token = tokenStorage.getAccessToken();
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    fetchUserSettings();
    fetchRecentActivity();
  }, []);

  const fetchUserSettings = async () => {
    try {
      const userResponse = await api.get('/api/auth/me');
      setProfileData(prev => ({
        ...prev,
        name: userResponse.data.user?.name || userResponse.data.name || '',
        email: userResponse.data.user?.email || userResponse.data.email || ''
      }));

      const userData = userResponse.data.user || userResponse.data;
      if (userData.sso_provider && userData.sso_provider !== 'local') {
        setSsoUser(true);
        setSsoProvider(userData.sso_provider);
      }

      const mfaResponse = await api.get('/api/mfa/status');
      setMfaEnabled(mfaResponse.data.mfaEnabled || false);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get('/api/auth/activity');
      setRecentActivity(response.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  const handleMfaSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/mfa/setup/initialize');
      setMfaSetup(response.data);
      setShowMfaSetup(true);
      toast.success('MFA setup initiated');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/mfa/setup/complete', { token: verificationCode });
      setMfaEnabled(true);
      setShowMfaSetup(false);
      setShowBackupCodes(true);
      toast.success('MFA enabled successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    setShowDisableConfirm(true);
  };

  const confirmMfaDisable = async () => {
    if (!disablePassword) {
      toast.error('Password is required');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/mfa/disable', { password: disablePassword });
      setMfaEnabled(false);
      setMfaSetup(null);
      setShowBackupCodes(false);
      setShowDisableConfirm(false);
      setDisablePassword('');
      toast.success('MFA disabled successfully');
    } catch (error: any) {
      console.error('MFA disable error:', error.response || error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      if (profileData.newPassword) {
        if (profileData.newPassword !== profileData.confirmPassword) {
          toast.error('New passwords do not match');
          setLoading(false);
          return;
        }

        if (!profileData.currentPassword) {
          toast.error('Current password is required');
          setLoading(false);
          return;
        }

        if (passwordErrors.length > 0) {
          toast.error('Password does not meet requirements');
          setLoading(false);
          return;
        }

        if (mfaEnabled && !passwordMfaCode && !showPasswordMfaInput) {
          setShowPasswordMfaInput(true);
          toast('Please enter your MFA code to change password', { icon: '🔐' });
          setLoading(false);
          return;
        }

        if (mfaEnabled && !passwordMfaCode) {
          toast.error('MFA code is required to change password');
          setLoading(false);
          return;
        }
      }

      let hasUpdates = false;

      if (profileData.name !== user?.name) {
        await api.put('/api/auth/profile', {
          name: profileData.name
        });
        hasUpdates = true;
      }

      if (profileData.newPassword) {
        const response = await api.post('/api/auth/change-password', {
          currentPassword: profileData.currentPassword,
          newPassword: profileData.newPassword,
          mfaCode: mfaEnabled ? passwordMfaCode : undefined
        });
        hasUpdates = true;

        if (response.data.requireRelogin) {
          toast.success('Password changed successfully. Please log in again.');
          tokenStorage.clearTokens();
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
          return;
        }
      }

      if (hasUpdates) {
        if (profileData.newPassword && profileData.name !== user?.name) {
          toast.success('Profile and password updated successfully');
        } else if (profileData.newPassword) {
          toast.success('Password changed successfully');
        } else {
          toast.success('Profile updated successfully');
        }
      }

      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setPasswordMfaCode('');
      setShowPasswordMfaInput(false);
    } catch (error: any) {
      if (error.response?.data?.mfaRequired) {
        setShowPasswordMfaInput(true);
        toast('Please enter your MFA code to change password', { icon: '🔐' });
      } else if (error.response?.data?.ssoRedirect) {
        toast.error(`Please change your password through ${error.response.data.ssoProvider}`);
      } else if (error.response?.data?.passwordErrors) {
        toast.error(`Password validation failed: ${error.response.data.passwordErrors.join(', ')}`);
      } else {
        toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!mfaSetup?.backup_codes) return;
    
    const content = `JARVIS RCSA - MFA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\nUser: ${user?.email}\n\n${mfaSetup.backup_codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nStore these codes safely. Each code can only be used once.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jarvis-mfa-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'activity', label: 'Activity', icon: Activity },
    ...(user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER' ? [
      { id: 'system-security', label: 'System Security', icon: SettingsIcon }
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SettingsIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          </div>
          <p className="text-gray-600">Manage your account settings and security preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Information</h2>
                    <p className="text-gray-600">Update your personal information and account details</p>
                  </div>

                  <div className="space-y-6">
                    {/* Profile Picture Section */}
                    <div className="flex items-center space-x-6 pb-6 border-b border-gray-200">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
                        <p className="text-gray-500">{user?.email}</p>
                        <div className="flex items-center mt-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user?.role === 'SUPER_ADMIN' ? 'bg-red-100 text-red-800' :
                            user?.role === 'MASTER' ? 'bg-purple-100 text-purple-800' :
                            user?.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            <Shield className="w-3 h-3 mr-1" />
                            {user?.role}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={profileData.email}
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                          placeholder="Email cannot be changed"
                        />
                      </div>
                    </div>

                    {/* Password Change Section */}
                    <div className="pt-6 border-t border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={profileData.currentPassword}
                            onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter current password"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={profileData.newPassword}
                            onChange={(e) => handlePasswordChange(e.target.value)}
                            onFocus={() => setShowPasswordRequirements(true)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              passwordErrors.length > 0 && profileData.newPassword ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Enter new password (12+ chars, 1 number, 1 special char)"
                          />

                          {/* Password Requirements */}
                          {(showPasswordRequirements || profileData.newPassword) && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                              <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                              <div className="grid grid-cols-1 gap-1 text-xs">
                                <div className={`flex items-center ${profileData.newPassword.length >= 12 ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className="mr-1">{profileData.newPassword.length >= 12 ? '✓' : '✗'}</span>
                                  At least 12 characters long
                                </div>
                                <div className={`flex items-center ${/\d/.test(profileData.newPassword) ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className="mr-1">{/\d/.test(profileData.newPassword) ? '✓' : '✗'}</span>
                                  At least one number (0-9)
                                </div>
                                <div className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(profileData.newPassword) ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className="mr-1">{/[!@#$%^&*(),.?":{}|<>]/.test(profileData.newPassword) ? '✓' : '✗'}</span>
                                  At least one special character (!@#$%^&*...)
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            value={profileData.confirmPassword}
                            onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Confirm new password"
                          />
                        </div>

                        {showPasswordMfaInput && mfaEnabled && (
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Smartphone className="inline w-4 h-4 mr-1" />
                              MFA Verification Code
                            </label>
                            <input
                              type="text"
                              value={passwordMfaCode}
                              onChange={(e) => setPasswordMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50"
                              placeholder="Enter 6-digit code from authenticator app"
                              maxLength={6}
                            />
                            <p className="text-sm text-blue-600 mt-1">
                              Enter the code from your authenticator app to verify this password change
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {ssoUser && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center">
                          <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                          <p className="text-yellow-800">
                            You are signed in via {ssoProvider}. Password changes must be made through your identity provider.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={handleProfileUpdate}
                        disabled={loading || (!!profileData.newPassword && passwordErrors.length > 0) || ssoUser}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Security Settings</h2>
                    <p className="text-gray-600">Manage your account security and authentication methods</p>
                  </div>

                  <div className="space-y-6">
                    {/* MFA Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Smartphone className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
                            <p className="text-gray-600">Add an extra layer of security to your account</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            mfaEnabled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {mfaEnabled ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Enabled
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Disabled
                              </>
                            )}
                          </span>
                        </div>
                      </div>

                      {!mfaEnabled && !showMfaSetup && (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <div className="flex items-center space-x-3 mb-2">
                              <Shield className="w-5 h-5 text-blue-600" />
                              <span className="font-medium text-gray-900">Enhanced Security</span>
                            </div>
                            <p className="text-gray-600 text-sm mb-4">
                              Two-factor authentication provides an additional security layer by requiring a code from your mobile device.
                            </p>
                            <ul className="text-sm text-gray-600 space-y-1 mb-4">
                              <li className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Works with Google Authenticator, Authy, and other TOTP apps</span>
                              </li>
                              <li className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Backup codes provided for account recovery</span>
                              </li>
                              <li className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Required for accessing sensitive features</span>
                              </li>
                            </ul>
                          </div>
                          <button
                            onClick={handleMfaSetup}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                          >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                            <span>Enable Two-Factor Authentication</span>
                          </button>
                        </div>
                      )}

                      {showMfaSetup && mfaSetup && (
                        <div className="space-y-6">
                          <div className="bg-white rounded-lg p-6 border border-blue-200">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Setup Two-Factor Authentication</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <h5 className="font-medium text-gray-900 mb-3">Step 1: Scan QR Code</h5>
                                <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center">
                                  <img 
                                    src={mfaSetup.qr_code}
                                    alt="MFA QR Code"
                                    className="w-48 h-48 mx-auto mb-3 border rounded-lg"
                                  />
                                  <p className="text-xs text-gray-600 text-center">
                                    Scan with Google Authenticator, Authy, or similar app
                                  </p>
                                </div>
                                
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Manual entry key:</span>
                                    <button
                                      onClick={() => copyToClipboard(mfaSetup.secret)}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      {copiedCode === mfaSetup.secret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                  </div>
                                  <code className="text-xs font-mono text-gray-800 break-all">{mfaSetup.secret || 'Loading...'}</code>
                                </div>
                              </div>

                              <div>
                                <h5 className="font-medium text-gray-900 mb-3">Step 2: Verify Setup</h5>
                                <div className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Enter 6-digit code from your app
                                    </label>
                                    <input
                                      type="text"
                                      value={verificationCode}
                                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl font-mono"
                                      placeholder="000000"
                                      maxLength={6}
                                    />
                                  </div>
                                  
                                  <div className="flex space-x-3">
                                    <button
                                      onClick={handleMfaVerification}
                                      disabled={loading || verificationCode.length !== 6}
                                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                    >
                                      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                      <span>Verify & Enable</span>
                                    </button>
                                    <button
                                      onClick={() => setShowMfaSetup(false)}
                                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {mfaEnabled && (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4 border border-green-200">
                            <div className="flex items-center space-x-3 mb-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-gray-900">Two-Factor Authentication Active</span>
                            </div>
                            <p className="text-gray-600 text-sm">
                              Your account is protected with two-factor authentication. You'll need your authenticator app to sign in.
                            </p>
                          </div>

                          <div className="flex space-x-3">
                            <button
                              onClick={() => setShowBackupCodes(!showBackupCodes)}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>{showBackupCodes ? 'Hide' : 'View'} Backup Codes</span>
                            </button>
                            <button
                              onClick={handleMfaDisable}
                              disabled={loading}
                              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                              <span>Disable MFA</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {showBackupCodes && mfaEnabled && (
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="w-5 h-5 text-yellow-600" />
                              <span className="font-medium text-gray-900">Backup Codes</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Backup codes are only displayed during MFA setup for security reasons. If you need new backup codes, disable and re-enable MFA.
                          </p>
                          <div className="text-sm text-blue-600">
                            <button
                              onClick={() => setShowBackupCodes(false)}
                              className="underline hover:text-blue-800"
                            >
                              Hide this message
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {showBackupCodes && mfaSetup?.backup_codes && (
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="w-5 h-5 text-yellow-600" />
                              <span className="font-medium text-gray-900">Backup Codes</span>
                            </div>
                            <button
                              onClick={downloadBackupCodes}
                              className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                            >
                              <Download className="w-4 h-4" />
                              <span className="text-sm">Download</span>
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Store these codes safely. Each can only be used once if you lose access to your authenticator app.
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {mfaSetup.backup_codes.map((code, index) => (
                              <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                                <code className="text-sm font-mono">{code}</code>
                                <button
                                  onClick={() => copyToClipboard(code)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {copiedCode === code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Session Security */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <Lock className="w-6 h-6 text-gray-600" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Session Security</h3>
                          <p className="text-gray-600">Manage your active sessions and security settings</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center space-x-2 mb-2">
                            <Globe className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-gray-900">Current Session</span>
                          </div>
                          <p className="text-sm text-gray-600">Active since login</p>
                          <p className="text-xs text-gray-500 mt-1">Auto-logout after 24 hours of inactivity</p>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center space-x-2 mb-2">
                            <Calendar className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-gray-900">Password Last Changed</span>
                          </div>
                          <p className="text-sm text-gray-600">Recently updated</p>
                          <p className="text-xs text-gray-500 mt-1">Strong password detected</p>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Activity</h2>
                    <p className="text-gray-600">Monitor your recent account activity and login history</p>
                  </div>

                  <div className="space-y-4">
                    {recentActivity.length > 0 ? (
                      recentActivity.map((activity) => (
                        <div key={activity.id} className="bg-gray-50 rounded-lg p-4 border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-full">
                                <Activity className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{activity.action}</p>
                                <p className="text-sm text-gray-600">
                                  {activity.ip_address} • {new Date(activity.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Recent Activity</h3>
                        <p className="text-gray-600">Your account activity will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* System Security Tab - Super Admin Only */}
              {activeTab === 'system-security' && (user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER') && (
                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <SettingsIcon className="w-6 h-6 text-red-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">System Security Settings</h2>
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                        SUPER ADMIN ONLY
                      </span>
                    </div>
                    <p className="text-gray-600">Configure system-wide security policies and compliance settings</p>
                  </div>

                  <SystemSecuritySettings />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MFA Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Disable Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600">This will make your account less secure</p>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter your password"
                autoFocus
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">Warning</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                Disabling MFA will make your account vulnerable to unauthorized access. You can re-enable it anytime.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDisableConfirm(false);
                  setDisablePassword('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmMfaDisable}
                disabled={loading || !disablePassword}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Disabling...</span>
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4" />
                    <span>Disable MFA</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;